import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Heart, MapPin, Clock, MessageCircle, ShieldCheck, ChevronLeft, ChevronRight, Share2, Flag, Ban, Truck, Package as PackageIcon, Loader2, HandCoins } from "lucide-react";
import api, { formatPrice, conditionLabel, deliveryLabel, thumbUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

export default function ListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthed, user } = useAuth();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(0);
  const [fav, setFav] = useState(false);
  const [showFull, setShowFull] = useState(false);
  const [starting, setStarting] = useState(false);
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/config").then((r) => setPaymentsEnabled(r.data.payments_enabled)).catch(() => {});
    api.get(`/listings/${id}`).then((r) => { setListing(r.data); setFav(r.data.is_favorite); setActive(0); })
      .catch(() => toast.error("Advertentie niet gevonden")).finally(() => setLoading(false));
  }, [id]);

  const toggleFav = async () => {
    if (!isAuthed) return navigate("/inloggen");
    const { data } = await api.post(`/listings/${id}/favorite`);
    setFav(data.is_favorite);
  };

  const startChat = async () => {
    if (!isAuthed) return navigate("/inloggen", { state: { from: `/advertentie/${id}` } });
    setStarting(true);
    try { const { data } = await api.post("/conversations", { listing_id: id }); navigate(`/chats/${data.id}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Kon chat niet starten"); }
    finally { setStarting(false); }
  };

  const report = async () => {
    if (!isAuthed) return navigate("/inloggen");
    await api.post("/report", { target_type: "listing", target_id: id, reason: "Verdachte advertentie" });
    toast.success("Melding ontvangen. Bedankt!");
  };

  const blockSeller = async () => {
    if (!isAuthed) return navigate("/inloggen");
    await api.post(`/block/${listing.seller.id}`);
    toast.success("Verkoper geblokkeerd");
  };

  if (loading) return <div className="flex min-h-[60vh] items-center justify-center"><div className="h-10 w-10 animate-spin rounded-full border-2 border-[#333] border-t-[#FF6A00]" /></div>;
  if (!listing) return null;

  const images = listing.images?.length ? listing.images : [null];
  const isOwn = user && listing.seller?.id === user.id;
  const sold = listing.status === "sold";
  const reserved = listing.status === "reserved";
  const reservedSince = listing.reserved_at ? new Date(listing.reserved_at).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" }) : "";
  // Offers only when the listing is active (older listings without allow_offers default to true).
  const offersAllowed = listing.allow_offers !== false && listing.status === "active";

  return (
    <div className="mx-auto max-w-6xl px-4 pb-28 pt-6 md:px-8 md:pb-12">
      <button onClick={() => navigate(-1)} data-testid="detail-back-btn" className="mb-4 flex items-center gap-1 text-sm text-[#A0A0A0] hover:text-[#F5F5F5]"><ChevronLeft className="h-4 w-4" /> Terug</button>

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        <div>
          <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border border-[#333] bg-[#2A2A2A]">
            {images[active] ? <img src={thumbUrl((listing.images_full && listing.images_full[active]) || images[active], 1000)} alt={listing.title} decoding="async" className="h-full w-full object-cover" data-testid="detail-main-image" /> : <div className="flex h-full items-center justify-center text-[#555]">Geen foto</div>}
            {(listing.status === "reserved" || sold) && <span className={`absolute left-3 top-3 rounded-full px-3 py-1 text-sm font-bold ${sold ? "bg-[#EF4444] text-white" : "bg-[#FFB27A] text-[#111]"}`}>{sold ? "Verkocht" : "Gereserveerd"}</span>}
            {images.length > 1 && (<>
              <button onClick={() => setActive((active - 1 + images.length) % images.length)} data-testid="gallery-prev" className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-[#111]/80 p-2 text-white backdrop-blur-sm hover:bg-[#111]"><ChevronLeft className="h-5 w-5" /></button>
              <button onClick={() => setActive((active + 1) % images.length)} data-testid="gallery-next" className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-[#111]/80 p-2 text-white backdrop-blur-sm hover:bg-[#111]"><ChevronRight className="h-5 w-5" /></button>
              <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">{images.map((img, i) => <span key={`dot-${img}-${i}`} className={`h-2 w-2 rounded-full ${i === active ? "bg-[#FF6A00]" : "bg-white/50"}`} />)}</div>
            </>)}
          </div>
          {images.length > 1 && <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">{images.map((img, i) => <button key={`thumb-${img}-${i}`} onClick={() => setActive(i)} className={`h-16 w-20 shrink-0 overflow-hidden rounded-lg border-2 ${i === active ? "border-[#FF6A00]" : "border-[#333]"}`}><img src={thumbUrl(img, 160)} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" /></button>)}</div>}
        </div>

        <div>
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-display text-3xl text-[#F5F5F5] sm:text-4xl">{listing.title}</h1>
            <div className="flex gap-2">
              <button onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success("Link gekopieerd"); }} data-testid="detail-share-btn" aria-label="Delen" className="rounded-full border border-[#333] p-2.5 text-[#F5F5F5] hover:border-[#FF6A00]"><Share2 className="h-5 w-5" /></button>
              <button onClick={toggleFav} data-testid="detail-favorite-btn" aria-label="Bewaren" className="rounded-full border border-[#333] p-2.5 hover:border-[#FF6A00]"><Heart className={`h-5 w-5 ${fav ? "fill-[#FF6A00] text-[#FF6A00]" : "text-[#F5F5F5]"}`} /></button>
            </div>
          </div>

          <p className="mt-2 font-display text-4xl text-[#FF6A00]" data-testid="detail-price">{formatPrice(listing.price)}</p>

          <div className="mt-4 flex flex-wrap gap-2">
            <Badge>{conditionLabel(listing.condition)}</Badge>
            <Badge>{deliveryLabel(listing.delivery)}</Badge>
            {listing.brand && <Badge>{listing.brand}</Badge>}
            {Object.entries(listing.specs || {}).map(([k, v]) => <Badge key={k}><span className="text-[#A0A0A0]">{k}:</span> {v}</Badge>)}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-[#A0A0A0]">
            <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {listing.location} · {listing.distance_km} km</span>
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {timeAgo(listing.created_at)}</span>
            {listing.delivery !== "ophalen" && listing.shipping_price > 0 && <span className="flex items-center gap-1"><Truck className="h-4 w-4" /> Verzenden {formatPrice(listing.shipping_price)}</span>}
          </div>

          <div className="mt-6">
            <h2 className="mb-2 font-display text-lg text-[#F5F5F5]">BESCHRIJVING</h2>
            <p className={`whitespace-pre-line text-[#cfcfcf] ${showFull ? "" : "line-clamp-3"}`} data-testid="detail-description">{listing.description}</p>
            {listing.description?.length > 140 && <button onClick={() => setShowFull(!showFull)} className="mt-1 text-sm font-semibold text-[#FF6A00]">{showFull ? "Minder lezen" : "Meer lezen"}</button>}
          </div>

          {listing.seller && (
            <div className="mt-6 flex items-center justify-between gap-3 rounded-xl border border-[#333] bg-[#1c1c1c] p-3" data-testid="detail-seller">
              <div className="flex items-center gap-3">
                <img src={listing.seller.avatar} alt="" className="h-11 w-11 rounded-full bg-[#2A2A2A]" />
                <div><p className="font-semibold text-[#F5F5F5]">{listing.seller.name}</p><p className="text-xs text-[#A0A0A0]">{listing.seller_type === "zakelijk" ? "Zakelijke verkoper" : "Particuliere verkoper"}</p></div>
              </div>
              {!isOwn && (
                <div className="flex gap-1">
                  <button onClick={report} data-testid="report-btn" aria-label="Melden" className="rounded-full p-2 text-[#A0A0A0] hover:text-[#FF6A00]"><Flag className="h-4 w-4" /></button>
                  <button onClick={blockSeller} data-testid="block-btn" aria-label="Blokkeren" className="rounded-full p-2 text-[#A0A0A0] hover:text-[#EF4444]"><Ban className="h-4 w-4" /></button>
                </div>
              )}
            </div>
          )}

          {reserved && (isOwn || listing.reserved_for_user_id === user?.id) && (
            <div data-testid="reserved-info" className="mt-6 rounded-xl border border-[#FFB27A]/40 bg-[#FFB27A]/10 px-4 py-3 text-sm text-[#FFB27A]">
              <p className="font-semibold">Gereserveerd sinds {reservedSince}</p>
              <p className="mt-0.5 text-xs text-[#FFB27A]/80">Reservering actief · verloopt na 7 dagen zonder activiteit in de chat.</p>
            </div>
          )}

          {!isOwn && reserved && listing.reserved_for_user_id !== user?.id && (
            <div data-testid="reserved-banner" className="mt-6 flex items-center gap-2 rounded-xl border border-[#FFB27A]/40 bg-[#FFB27A]/10 px-4 py-3 text-sm text-[#FFB27A]">
              <ShieldCheck className="h-4 w-4 shrink-0" /> Deze advertentie is gereserveerd. Bieden is tijdelijk uitgeschakeld — je kunt de verkoper nog wel een bericht sturen.
            </div>
          )}

          {!isOwn && !sold && (
            <div className="mt-6 hidden gap-3 md:flex md:flex-col">
              <button onClick={startChat} disabled={starting} data-testid="detail-chat-btn" className="flex items-center justify-center gap-2 rounded-full bg-[#FF6A00] py-3.5 font-bold text-[#111] transition-all hover:bg-[#FFB27A] active:scale-95 disabled:opacity-60"><MessageCircle className="h-5 w-5" /> {starting ? "Bezig..." : "Chat met verkoper"}</button>
              {offersAllowed && <button onClick={() => (isAuthed ? setOfferOpen(true) : navigate("/inloggen"))} data-testid="detail-offer-btn" className="flex items-center justify-center gap-2 rounded-full border border-[#333] py-3.5 font-bold text-[#F5F5F5] transition-colors hover:border-[#FF6A00] hover:text-[#FF6A00]"><HandCoins className="h-5 w-5" /> Doe een bod</button>}
              <button data-testid="detail-pay-btn" onClick={() => setPayOpen(true)} className="flex items-center justify-center gap-2 rounded-full border border-[#FF6A00] py-3.5 font-bold text-[#FF6A00] transition-colors hover:bg-[#FF6A00]/10"><ShieldCheck className="h-5 w-5" /> Veilig betalen</button>
            </div>
          )}

          {isOwn && !sold && (
            <div className="mt-6 hidden gap-2 md:flex md:flex-col">
              <button disabled data-testid="detail-pay-owner" className="flex cursor-not-allowed items-center justify-center gap-2 rounded-full border border-[#333] py-3.5 font-bold text-[#777]"><ShieldCheck className="h-5 w-5" /> Veilig betalen</button>
              <p className="text-center text-xs text-[#A0A0A0]">Dit is jouw advertentie — zo zien kopers de betaalknop.</p>
            </div>
          )}
        </div>
      </div>

      {!isOwn && !sold && (
        <div className="fixed bottom-[60px] left-0 right-0 z-40 flex gap-2 border-t border-[#333] bg-[#111]/95 p-3 backdrop-blur-xl md:hidden">
          <button onClick={startChat} disabled={starting} data-testid="detail-chat-btn-mobile" className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#FF6A00] py-3 font-bold text-[#111] active:scale-95"><MessageCircle className="h-5 w-5" /> Chat</button>
          {offersAllowed && <button onClick={() => (isAuthed ? setOfferOpen(true) : navigate("/inloggen"))} data-testid="detail-offer-btn-mobile" className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#333] py-3 font-bold text-[#F5F5F5] active:scale-95"><HandCoins className="h-5 w-5" /> Bod</button>}
          <button onClick={() => setPayOpen(true)} className="flex flex-1 items-center justify-center gap-2 rounded-full border border-[#FF6A00] py-3 font-bold text-[#FF6A00]"><ShieldCheck className="h-5 w-5" /> Betalen</button>
        </div>
      )}

      {isOwn && !sold && (
        <div className="fixed bottom-[60px] left-0 right-0 z-40 flex gap-2 border-t border-[#333] bg-[#111]/95 p-3 backdrop-blur-xl md:hidden">
          <button disabled data-testid="detail-pay-owner-mobile" className="flex flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-full border border-[#333] py-3 font-bold text-[#777]"><ShieldCheck className="h-5 w-5" /> Dit is jouw advertentie</button>
        </div>
      )}

      <PaymentDialog open={payOpen} onClose={() => setPayOpen(false)} listing={listing} enabled={paymentsEnabled} isAuthed={isAuthed} navigate={navigate} />
      <OfferDialog open={offerOpen} onClose={() => setOfferOpen(false)} listing={listing} />
    </div>
  );
}

function OfferDialog({ open, onClose, listing }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const value = parseFloat(amount);
    if (!value || value <= 0) return toast.error("Voer een geldig bedrag in");
    setBusy(true);
    try {
      await api.post("/offers", { listing_id: listing.id, amount: value });
      toast.success("Bod verstuurd! De verkoper krijgt een melding.");
      setAmount("");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Bod plaatsen mislukt"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]" data-testid="offer-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-2xl"><HandCoins className="h-6 w-6 text-[#FF6A00]" /> DOE EEN BOD</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <p className="text-sm text-[#A0A0A0]">Vraagprijs: <span className="font-semibold text-[#F5F5F5]">{formatPrice(listing.price)}</span></p>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#777]">€</span>
            <input autoFocus type="number" min="1" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Jouw bod" data-testid="offer-amount-input" className="w-full rounded-lg border border-[#333] bg-[#2A2A2A] py-3 pl-8 pr-3 text-lg text-[#F5F5F5] outline-none focus:border-[#FF6A00]" />
          </div>
          <button onClick={submit} disabled={busy} data-testid="offer-submit-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF6A00] py-3.5 font-bold text-[#111] disabled:opacity-60">{busy && <Loader2 className="h-5 w-5 animate-spin" />} Verstuur bod</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PaymentDialog({ open, onClose, listing, enabled, isAuthed, navigate }) {
  const [delivery, setDelivery] = useState(listing.delivery === "verzenden" ? "verzenden" : "ophalen");
  const [busy, setBusy] = useState(false);

  const pay = async () => {
    if (!isAuthed) return navigate("/inloggen");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/create", { listing_id: listing.id, delivery_method: delivery });
      if (!data.payments_enabled) { toast.info("Betalen is binnenkort beschikbaar"); onClose(); return; }
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Betaling mislukt"); }
    finally { setBusy(false); }
  };

  const total = listing.price + (delivery === "verzenden" ? (listing.shipping_price || 0) : 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="border-[#333] bg-[#1c1c1c] text-[#F5F5F5]" data-testid="payment-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-display text-2xl"><ShieldCheck className="h-6 w-6 text-[#FF6A00]" /> VEILIG BETALEN</DialogTitle></DialogHeader>
        {!enabled ? (
          <div className="py-4 text-center" data-testid="payments-coming-soon">
            <span className="inline-block rounded-full bg-[#2A2A2A] px-4 py-1.5 text-sm font-semibold text-[#FFB27A]">Binnenkort beschikbaar</span>
            <p className="mt-4 text-[#A0A0A0]">Online betalen via iDEAL en creditcard wordt binnenkort geactiveerd. Neem tot die tijd contact op met de verkoper via de chat.</p>
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {listing.delivery === "beide" && (
              <div><p className="mb-2 text-sm font-semibold">Levering</p>
                <RadioGroup value={delivery} onValueChange={setDelivery} className="flex gap-3">
                  <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-[#333] p-3"><RadioGroupItem value="ophalen" className="h-5 w-5 border-white/70 text-white" /><PackageIcon className="h-4 w-4" /> Ophalen</label>
                  <label className="flex flex-1 cursor-pointer items-center gap-2 rounded-lg border border-[#333] p-3"><RadioGroupItem value="verzenden" className="h-5 w-5 border-white/70 text-white" /><Truck className="h-4 w-4" /> Verzenden</label>
                </RadioGroup>
              </div>
            )}
            <div className="rounded-lg border border-[#333] bg-[#2A2A2A]/40 p-3 text-sm text-[#A0A0A0]" data-testid="payment-methods-note">
              Je kiest je betaalmethode (iDEAL, Klarna of Pay by Bank) veilig op de volgende pagina.
            </div>
            <div className="flex items-center justify-between border-t border-[#333] pt-3"><span className="text-[#A0A0A0]">Totaal</span><span className="font-display text-2xl text-[#FF6A00]">{formatPrice(total)}</span></div>
            <button onClick={pay} disabled={busy} data-testid="pay-confirm-btn" className="flex w-full items-center justify-center gap-2 rounded-full bg-[#FF6A00] py-3.5 font-bold text-[#111]">{busy && <Loader2 className="h-5 w-5 animate-spin" />} Betaal {formatPrice(total)}</button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Badge({ children }) { return <span className="rounded-full border border-[#333] bg-[#2A2A2A] px-3 py-1.5 text-xs font-semibold text-[#F5F5F5]">{children}</span>; }
function timeAgo(iso) { const d = (Date.now() - new Date(iso).getTime()) / 1000; if (d < 3600) return "Vandaag"; if (d < 86400) return `${Math.floor(d / 3600)} uur geleden`; const days = Math.floor(d / 86400); return days === 1 ? "Gisteren" : `${days} dagen geleden`; }
